import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import PerformanceMetricCard from '../../governance-performance-dashboard/components/PerformanceMetricCard';
import PerformanceTrendChart from '../../governance-performance-dashboard/components/PerformanceTrendChart';
import RiskMatrixPanel from '../../governance-performance-dashboard/components/RiskMatrixPanel';
import CAPAActionsTable from '../../governance-performance-dashboard/components/CAPAActionsTable';
import RiskPredictionPanel from '../../governance-performance-dashboard/components/RiskPredictionPanel';
import { risksService, capaService, metricsService } from '../../../services/partnerService';

// ─── Add CAPA Modal ───────────────────────────────────────────────────────────
const AddCAPAModal = ({ onClose, onSuccess, partnerId }) => {
  const [form, setForm] = useState({
    description: '',
    root_cause: '',
    action_type: 'corrective',
    owner: '',
    due_date: '',
    priority: 'medium',
    capa_status: 'open',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.description?.trim()) { setError('Description is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await capaService?.create({
        partner_id: partnerId || null,
        description: form?.description,
        root_cause: form?.root_cause,
        action_type: form?.action_type,
        owner: form?.owner,
        due_date: form?.due_date || null,
        priority: form?.priority,
        capa_status: form?.capa_status,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create CAPA action.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="ListChecks" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Log CAPA Action</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-28 space-y-16 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Issue / Description *</label>
            <textarea name="description" value={form?.description} onChange={handleChange} rows={3} placeholder="Describe the issue..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Root Cause</label>
            <textarea name="root_cause" value={form?.root_cause} onChange={handleChange} rows={2} placeholder="What is the root cause?" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Action Type</label>
              <select name="action_type" value={form?.action_type} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                <option value="corrective">Corrective</option>
                <option value="preventive">Preventive</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Priority</label>
              <select name="priority" value={form?.priority} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['low', 'medium', 'high', 'critical']?.map(p => <option key={p} value={p}>{p?.charAt(0)?.toUpperCase() + p?.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Owner</label>
              <input name="owner" value={form?.owner} onChange={handleChange} placeholder="e.g. Jane Doe" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Due Date</label>
              <input name="due_date" type="date" value={form?.due_date} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Plus" size={16} /> Log CAPA</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Add Risk Modal ───────────────────────────────────────────────────────────
const AddRiskModal = ({ onClose, onSuccess, partnerId }) => {
  const [form, setForm] = useState({
    title: '',
    category: 'Operational',
    description: '',
    severity: 'medium',
    owner: '',
    review_date: '',
    mitigation: '',
    risk_status: 'open',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Risk title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await risksService?.create({
        partner_id: partnerId || null,
        title: form?.title,
        category: form?.category,
        description: form?.description,
        severity: form?.severity,
        owner: form?.owner,
        review_date: form?.review_date || null,
        mitigation: form?.mitigation,
        risk_status: form?.risk_status,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to log risk.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-red-50 to-orange-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Icon name="Shield" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Log Risk</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-28 space-y-16 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-16">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-6">Risk Title *</label>
              <input name="title" value={form?.title} onChange={handleChange} placeholder="e.g. SLA Compliance Risk" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Category</label>
              <select name="category" value={form?.category} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['Operational', 'Financial', 'Compliance', 'Strategic', 'Technical', 'Reputational']?.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Severity</label>
              <select name="severity" value={form?.severity} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['low', 'medium', 'high', 'critical']?.map(s => <option key={s} value={s}>{s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Owner</label>
              <input name="owner" value={form?.owner} onChange={handleChange} placeholder="e.g. Risk Manager" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Review Date</label>
              <input name="review_date" type="date" value={form?.review_date} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-6">Description</label>
              <textarea name="description" value={form?.description} onChange={handleChange} rows={2} placeholder="Describe the risk..." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-6">Mitigation Plan</label>
              <textarea name="mitigation" value={form?.mitigation} onChange={handleChange} rows={2} placeholder="How will this risk be mitigated?" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 resize-none" />
            </div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Plus" size={16} /> Log Risk</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Governance Tab ───────────────────────────────────────────────────────────
const GovernanceTab = ({ partnerId, partnerName }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [risks, setRisks] = useState([]);
  const [capaActions, setCAPAActions] = useState([]);
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddCAPAModal, setShowAddCAPAModal] = useState(false);
  const [showAddRiskModal, setShowAddRiskModal] = useState(false);

  useEffect(() => {
    if (partnerId) loadData();
  }, [partnerId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [risksData, capaData, metricsData] = await Promise.all([
        risksService?.getByPartner ? risksService?.getByPartner(partnerId) : risksService?.getAll(),
        capaService?.getByPartner ? capaService?.getByPartner(partnerId) : capaService?.getAll(),
        metricsService?.getByPartner ? metricsService?.getByPartner(partnerId) : (metricsService?.getAll ? metricsService?.getAll() : [])
      ]);
      // Filter by partner if bulk fetch was used
      setRisks((risksData || [])?.filter(r => !r?.partner_id || r?.partner_id === partnerId));
      setCAPAActions((capaData || [])?.filter(c => !c?.partner_id || c?.partner_id === partnerId));
      setMetricsHistory(metricsData || []);
    } catch (err) {
      console.error('Error loading governance data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const latestMetrics = metricsHistory?.[metricsHistory?.length - 1];
  const prevMetrics = metricsHistory?.[metricsHistory?.length - 2];

  const performanceMetrics = [
    {
      title: "Revenue Contribution",
      value: latestMetrics ? `OMR ${(latestMetrics?.revenue / 1000)?.toFixed(0)}K` : "OMR 0",
      unit: "OMR",
      trend: latestMetrics && prevMetrics ? (latestMetrics?.revenue > prevMetrics?.revenue ? "up" : "down") : "up",
      trendValue: latestMetrics && prevMetrics ? `${(((latestMetrics?.revenue - prevMetrics?.revenue) / prevMetrics?.revenue) * 100)?.toFixed(1)}%` : "0%",
      benchmark: latestMetrics ? `OMR ${(latestMetrics?.target_revenue / 1000)?.toFixed(0)}K` : "OMR 0",
      icon: "DollarSign",
      color: "primary"
    },
    {
      title: "Win Rate",
      value: latestMetrics ? String(latestMetrics?.win_rate) : "0",
      unit: "%",
      trend: "up",
      trendValue: "+5.3%",
      benchmark: "65%",
      icon: "Target",
      color: "success"
    },
    {
      title: "Delivery Quality",
      value: latestMetrics ? String(latestMetrics?.delivery_quality) : "0",
      unit: "/5.0",
      trend: "up",
      trendValue: "+0.2",
      benchmark: "4.5",
      icon: "Award",
      color: "warning"
    },
    {
      title: "SLA Compliance",
      value: latestMetrics ? String(latestMetrics?.sla_compliance) : "0",
      unit: "%",
      trend: latestMetrics && prevMetrics ? (latestMetrics?.sla_compliance >= prevMetrics?.sla_compliance ? "up" : "down") : "up",
      trendValue: "-2.1%",
      benchmark: "95%",
      icon: "CheckCircle2",
      color: "error"
    },
    {
      title: "Client Feedback",
      value: latestMetrics ? String(latestMetrics?.client_feedback) : "0",
      unit: "/5.0",
      trend: "up",
      trendValue: "+0.3",
      benchmark: "4.5",
      icon: "ThumbsUp",
      color: "success"
    }
  ];

  const revenueData = metricsHistory?.map(m => ({
    month: new Date(m?.metric_month)?.toLocaleString('default', { month: 'short' }),
    revenue: m?.revenue,
    target: m?.target_revenue
  }));

  const performanceData = metricsHistory?.map(m => ({
    month: new Date(m?.metric_month)?.toLocaleString('default', { month: 'short' }),
    winRate: m?.win_rate,
    slaCompliance: m?.sla_compliance,
    deliveryQuality: m?.delivery_quality
  }));

  const riskPredictions = [
    {
      id: 1,
      title: "Projected SLA Compliance Drop",
      description: "AI analysis predicts potential SLA compliance decrease to 89% in next quarter based on current resource allocation and project pipeline.",
      severity: "high",
      probability: 72,
      impact: "Revenue impact of OMR 150K due to penalty clauses and potential tier downgrade affecting future opportunities.",
      recommendation: "Increase technical support staffing by 2 FTEs and implement proactive monitoring system for early issue detection.",
      actionType: "immediate",
      actionLabel: "Review Staffing Plan"
    },
    {
      id: 2,
      title: "Client Churn Risk",
      description: "Engagement metrics show 40% decrease in interaction frequency and delayed response to renewal discussions.",
      severity: "critical",
      probability: 65,
      impact: "Loss of OMR 800K annual revenue and strategic reference account in financial services sector.",
      recommendation: "Schedule executive-level review meeting within 7 days and prepare customized retention proposal.",
      actionType: "escalate",
      actionLabel: "Escalate to Leadership"
    },
    {
      id: 3,
      title: "Certification Gap Impact",
      description: "Current certification renewal timeline creates a gap where partner may not meet tier requirements.",
      severity: "medium",
      probability: 85,
      impact: "Temporary tier downgrade affecting deal registration priority and co-marketing fund eligibility.",
      recommendation: "Expedite certification renewal process and request temporary waiver based on historical performance.",
      actionType: "scheduled",
      actionLabel: "Schedule Review"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'risks', label: 'Risk Management', icon: 'Shield' },
    { id: 'actions', label: 'CAPA Actions', icon: 'ListChecks' },
    { id: 'predictions', label: 'AI Predictions', icon: 'Brain' }
  ];

  return (
    <div className="space-y-24">
      {/* Governance Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-16">
        <div className="flex items-center gap-12">
          <div className="p-10 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-md">
            <Icon name="Shield" size={22} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Governance & Performance</h2>
            <p className="text-sm text-slate-500 mt-2">
              {partnerName ? `Real-time monitoring for ${partnerName}` : 'Real-time monitoring and risk management'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-10 flex-wrap">
          <button
            onClick={() => setShowAddCAPAModal(true)}
            className="flex items-center gap-8 px-18 py-10 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
          >
            <Icon name="Plus" size={15} />
            Log CAPA
          </button>
          <button
            onClick={() => setShowAddRiskModal(true)}
            className="flex items-center gap-8 px-18 py-10 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
          >
            <Icon name="Shield" size={15} />
            Log Risk
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-8 overflow-x-auto pb-2">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center gap-10 px-20 py-12 rounded-xl font-semibold text-sm whitespace-nowrap transition-all
              ${activeTab === tab?.id
                ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-lg shadow-primary/25'
                : 'bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-slate-200'}
            `}
          >
            <Icon name={tab?.icon} size={16} />
            {tab?.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-48">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-12 p-20 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <Icon name="AlertCircle" size={18} />
          <span>Error: {error}</span>
          <button onClick={loadData} className="ml-auto font-medium underline">Retry</button>
        </div>
      )}

      {!loading && activeTab === 'overview' && (
        <div className="space-y-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-16">
            {performanceMetrics?.map((metric, index) => (
              <PerformanceMetricCard key={index} {...metric} />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
              <h3 className="text-lg font-bold text-slate-900 mb-20">Revenue Performance</h3>
              {revenueData?.length > 0 ? (
                <PerformanceTrendChart
                  title="Revenue Performance"
                  data={revenueData}
                  dataKeys={[{ key: 'revenue', color: '#3B82F6', name: 'Revenue' }, { key: 'target', color: '#8B5CF6', name: 'Target' }]}
                  xAxisKey="month"
                  yAxisLabel="Amount (OMR)"
                />
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No metrics data available</div>
              )}
            </div>
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
              <h3 className="text-lg font-bold text-slate-900 mb-20">Performance Metrics Trend</h3>
              {performanceData?.length > 0 ? (
                <PerformanceTrendChart
                  title="Performance Metrics Trend"
                  data={performanceData}
                  dataKeys={[
                    { key: 'winRate', color: '#10B981', name: 'Win Rate (%)' },
                    { key: 'slaCompliance', color: '#3B82F6', name: 'SLA Compliance (%)' },
                    { key: 'deliveryQuality', color: '#F59E0B', name: 'Quality Score' }
                  ]}
                  xAxisKey="month"
                  yAxisLabel="Score"
                />
              ) : (
                <div className="flex items-center justify-center h-48 text-slate-400 text-sm">No metrics data available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'risks' && (
        <div className="space-y-24">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
            <RiskMatrixPanel risks={risks} onUpdateRisk={() => {}} />
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
            <RiskPredictionPanel predictions={riskPredictions} />
          </div>
        </div>
      )}

      {!loading && activeTab === 'actions' && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
          <CAPAActionsTable
            actions={capaActions}
            onUpdateAction={() => {}}
            onUpdateStatus={() => {}}
            onViewDetails={() => {}}
          />
        </div>
      )}

      {!loading && activeTab === 'predictions' && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200/60 p-24">
          <RiskPredictionPanel predictions={riskPredictions} />
        </div>
      )}

      {showAddCAPAModal && (
        <AddCAPAModal
          onClose={() => setShowAddCAPAModal(false)}
          onSuccess={() => { setShowAddCAPAModal(false); loadData(); }}
          partnerId={partnerId}
        />
      )}
      {showAddRiskModal && (
        <AddRiskModal
          onClose={() => setShowAddRiskModal(false)}
          onSuccess={() => { setShowAddRiskModal(false); loadData(); }}
          partnerId={partnerId}
        />
      )}
    </div>
  );
};

export default GovernanceTab;
